import type { NavigationGuardNext, RouteLocationNormalized, RouteRecordRaw } from "vue-router";
import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "./stores/auth";

// Lazy-load every page so the initial bundle stays small. Chunks are grouped
// by feature via `webpackChunkName` so e.g. visiting /seeds preloads create + update too.
const Site = () => import(/* webpackChunkName: "site-shell" */ "./pages/Site");
const Landing = () => import(/* webpackChunkName: "site-shell" */ "./pages/Site/pages/Landing.vue");
const Login = () => import(/* webpackChunkName: "site-shell" */ "./pages/Site/pages/Login.vue");
const Privacy = () => import(/* webpackChunkName: "site-shell" */ "./pages/Site/pages/Privacy.vue");
const Credits = () => import(/* webpackChunkName: "site-shell" */ "./pages/Site/pages/Credits.vue");
const Signup = () => import(/* webpackChunkName: "site-shell" */ "./pages/Site/pages/Signup.vue");
// Public classification page. Chunked with "taxonomy" so the D3 tree lib loads
// only on this route (not in the site shell / landing / login).
const Classification = () => import(/* webpackChunkName: "taxonomy" */ "./pages/Site/pages/Classification.vue");
const SoilCalculator = () => import(/* webpackChunkName: "site-shell" */ "./pages/Site/pages/SoilCalculator.vue");

const App = () => import(/* webpackChunkName: "app-shell" */ "./pages/App");
const Home = () => import(/* webpackChunkName: "home" */ "./pages/App/pages/Home");

const Seeds = () => import(/* webpackChunkName: "seeds" */ "./pages/App/pages/Seeds/Seeds.vue");
const SeedList = () => import(/* webpackChunkName: "seeds" */ "./pages/App/pages/Seeds/SeedList.vue");
const SeedCreate = () => import(/* webpackChunkName: "seeds" */ "./pages/App/pages/Seeds/SeedCreate.vue");
const SeedUpdate = () => import(/* webpackChunkName: "seeds" */ "./pages/App/pages/Seeds/SeedUpdate.vue");

const Crops = () => import(/* webpackChunkName: "crops" */ "./pages/App/pages/Crops");
const CropHome = () => import(/* webpackChunkName: "crops" */ "./pages/App/pages/Crops/CropHome.vue");
const CropDetail = () => import(/* webpackChunkName: "crops" */ "./pages/App/pages/Crops/pages/CropDetail");
const CropCreate = () => import(/* webpackChunkName: "crops" */ "./pages/App/pages/Crops/pages/CropCreate");

const Garden = () => import(/* webpackChunkName: "garden" */ "./pages/App/pages/Garden");
const PlanEdit = () => import(/* webpackChunkName: "garden" */ "./pages/App/pages/Garden/plan/PlanEdit.vue");
const Occupation = () => import(/* webpackChunkName: "occupation" */ "./pages/App/pages/Occupation/Occupation.vue");
const Watering = () => import(/* webpackChunkName: "watering" */ "./pages/App/pages/Watering/Watering.vue");

const Harvests = () => import(/* webpackChunkName: "harvests" */ "./pages/App/pages/Harvests");

const Tasks = () => import(/* webpackChunkName: "tasks" */ "./pages/App/pages/Tasks");
const TaskCreate = () => import(/* webpackChunkName: "tasks" */ "./pages/App/pages/Tasks/pages/TaskCreate");

const TaskTimeline = () => import(/* webpackChunkName: "calendar" */ "./pages/App/pages/Timeline");
const SelfSufficiency = () =>
  import(/* webpackChunkName: "autosuffisance" */ "./pages/App/pages/SelfSufficiency/SelfSufficiency.vue");
const User = () => import(/* webpackChunkName: "user" */ "./pages/App/pages/User");
const TaxonList = () => import(/* webpackChunkName: "plants" */ "./pages/App/pages/Taxa/TaxonList.vue");
const Taxonomy = () => import(/* webpackChunkName: "taxonomy" */ "./pages/App/pages/Taxonomy");

const authNeededBeforeEnter = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  if (useAuthStore().isAuthenticated) {
    next();
  } else {
    next({ name: "login" });
  }
};

const rejectAuthBeforeEnter = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  if (useAuthStore().isAuthenticated) {
    next({ name: "home" });
  } else {
    next();
  }
};

const routes: RouteRecordRaw[] = [
  // Website pages
  {
    path: "/",
    component: Site,
    children: [
      {
        path: "",
        component: Landing,
        name: "landing",
      },
      {
        path: "signup",
        component: Signup,
        name: "signup",
        beforeEnter: rejectAuthBeforeEnter,
      },
      {
        path: "login",
        component: Login,
        name: "login",
        beforeEnter: rejectAuthBeforeEnter,
      },
      {
        path: "confidentialite",
        component: Privacy,
        name: "privacy",
      },
      {
        path: "credits",
        component: Credits,
        name: "credits",
      },
      {
        path: "classification",
        component: Classification,
        name: "classification",
      },
      {
        path: "texture-du-sol",
        component: SoilCalculator,
        name: "soilCalculator",
      },
    ],
  },
  // App pages
  {
    path: "/app",
    component: App,
    beforeEnter: authNeededBeforeEnter,
    children: [
      {
        path: "",
        component: Home,
        name: "home",
      },
      {
        path: "/seeds",
        component: Seeds,
        children: [
          { path: "", component: SeedList, name: "seeds" },
          { path: "create", component: SeedCreate, name: "seedCreate" },
          { path: ":id(\\d+)/update", component: SeedUpdate, name: "seedUpdate" },
        ],
      },
      {
        path: "crops",
        component: Crops,
        children: [
          { path: "", component: CropHome, name: "crops" },
          { path: "create", component: CropCreate, name: "cropCreate" },
          { path: ":id(\\d+)", component: CropDetail, name: "cropDetail" },
        ],
      },
      {
        path: "/plants",
        component: TaxonList,
        name: "plants",
      },
      {
        path: "/garden/:gardenId(\\d+)",
        component: Garden,
        children: [
          {
            path: "plan",
            component: PlanEdit,
            name: "plan",
          },
          {
            path: "occupation",
            component: Occupation,
            name: "occupation",
          },
          {
            path: "watering",
            component: Watering,
            name: "watering",
          },
        ],
      },
      {
        path: "/harvests",
        component: Harvests,
        name: "harvests",
      },
      {
        path: "/tasks",
        component: Tasks,
        name: "tasks",
      },
      {
        path: "/tasks/create",
        component: TaskCreate,
        name: "taskCreate",
      },
      {
        path: "/calendar",
        component: TaskTimeline,
        name: "calendar",
      },
      {
        path: "/autosuffisance",
        component: SelfSufficiency,
        name: "autosuffisance",
      },
      {
        path: "/user",
        component: User,
        name: "user",
      },
      {
        path: "/taxonomy",
        component: Taxonomy,
        name: "taxonomy",
      },
    ],
  },
  { path: "/:catchAll(.*)", redirect: { name: "landing" } },
];

export default createRouter({
  routes,
  history: createWebHistory(),
  scrollBehavior(to, from, savedPosition) {
    // Restore previous position on back/forward, otherwise scroll to top.
    return savedPosition ?? { top: 0 };
  },
});
